import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Sparkles, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { articles, getArticleBySlug } from "@/data/articles";

const SITE_ORIGIN = "https://cosomicsage.com";

/**
 * Render a section body safely. We split on blank lines; any chunk that starts
 * with a block element (<ul>, <ol>, <pre>) is injected as-is, while plain-text
 * chunks are wrapped in a <p>. This avoids the invalid HTML of wrapping block
 * elements inside <p>, which the browser otherwise auto-closes and leaves
 * empty <p> ghosts in the DOM.
 */
function renderBody(body) {
  // Split on blank lines, OR on a single newline that immediately precedes a
  // block-level tag — this avoids ever wrapping a <ul>/<ol>/<pre> inside a <p>
  // (which is invalid HTML and causes browser auto-close weirdness).
  const chunks = body.split(/\n\n+|\n(?=<(?:ul|ol|pre|blockquote|h\d))/i);
  return chunks.map((chunk, i) => {
    const trimmed = chunk.trim();
    if (!trimmed) return null;
    const isBlock = /^<(ul|ol|pre|blockquote|h\d)/i.test(trimmed);
    if (isBlock) {
      return <div key={i} className="my-4" dangerouslySetInnerHTML={{ __html: trimmed }} />;
    }
    return <p key={i} className="mb-4" dangerouslySetInnerHTML={{ __html: trimmed }} />;
  });
}

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function Article() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return;
    const url = `${SITE_ORIGIN}/learn/${article.slug}`;
    document.title = `${article.title} | Cosomic Sage`;
    setMeta('meta[name="description"]', "content", article.description);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:title"]', "content", article.title);
    setMeta('meta[property="og:description"]', "content", article.description);
    setMeta('meta[property="og:type"]', "content", "article");
  }, [article]);

  if (!article) return <Navigate to="/learn" replace />;

  const idx = articles.findIndex((a) => a.slug === slug);
  const prev = idx > 0 ? articles[idx - 1] : null;
  const next = idx < articles.length - 1 ? articles[idx + 1] : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 starfield" data-testid="article-page">
      <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-serif text-lg text-slate-50">Cosmic Sage</span>
          </Link>
          <Link to="/learn" className="text-sm text-amber-300 hover:text-amber-200" data-testid="article-back-learn">
            <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> All Guides
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 text-xs text-slate-500 mb-4" data-testid="article-meta">
          <span className="text-amber-300/80 uppercase tracking-wider">{article.category}</span>
          <span className="text-slate-700">·</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> {article.readMinutes} min read
          </span>
          <span className="text-slate-700">·</span>
          <time dateTime={article.date}>
            {new Date(article.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </time>
        </div>

        <h1 className="font-serif text-4xl md:text-5xl text-slate-50 leading-tight mb-5" data-testid="article-title">
          {article.title}
        </h1>
        <p className="text-lg text-slate-400 leading-relaxed border-l-2 border-amber-500/40 pl-5 mb-10" data-testid="article-description">
          {article.description}
        </p>

        <div className="space-y-10 text-slate-300 leading-relaxed" data-testid="article-body">
          {article.sections.map((s, i) => (
            <section key={i} className="scroll-mt-24">
              <h2 className="font-serif text-2xl md:text-3xl text-slate-50 mb-4">{s.heading}</h2>
              <div className="text-slate-300 leading-[1.75] [&_pre]:bg-slate-900 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:text-sm [&_pre]:text-amber-200 [&_pre]:border [&_pre]:border-white/5 [&_pre]:my-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ol]:my-4 [&_b]:text-amber-200 [&_b]:font-semibold [&_i]:text-slate-200 [&_li]:leading-relaxed">
                {renderBody(s.body)}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-16 p-6 rounded-2xl bg-amber-500/5 border border-amber-500/20">
          <h3 className="font-serif text-xl text-slate-50 mb-2">Try it on your own chart</h3>
          <p className="text-sm text-slate-400 mb-4">
            Cosmic Sage computes everything described in this guide for your real birth date — for free.
          </p>
          <Link
            to="/dashboard"
            data-testid="article-cta-dashboard"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-medium"
          >
            Open the app <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <nav className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="article-nav">
          {prev && (
            <Link to={`/learn/${prev.slug}`} className="p-5 rounded-xl bg-slate-900/50 border border-white/5 hover:border-amber-500/30 transition-colors">
              <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Previous
              </div>
              <div className="text-sm text-slate-200 line-clamp-2">{prev.title}</div>
            </Link>
          )}
          {next && (
            <Link to={`/learn/${next.slug}`} className="p-5 rounded-xl bg-slate-900/50 border border-white/5 hover:border-amber-500/30 transition-colors md:text-right">
              <div className="text-xs text-slate-500 mb-1 flex items-center gap-1 md:justify-end">
                Next <ArrowRight className="w-3 h-3" />
              </div>
              <div className="text-sm text-slate-200 line-clamp-2">{next.title}</div>
            </Link>
          )}
        </nav>

        <footer className="mt-12 pt-6 border-t border-white/5 text-sm text-slate-500 flex flex-wrap gap-4">
          <Link to="/" className="hover:text-amber-300">Home</Link>
          <Link to="/learn" className="hover:text-amber-300">All Guides</Link>
          <Link to="/about" className="hover:text-amber-300">About</Link>
          <Link to="/privacy" className="hover:text-amber-300">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-amber-300">Terms of Service</Link>
          <a href="mailto:support@cosomicsage.com" className="hover:text-amber-300">Contact</a>
        </footer>
      </article>
    </div>
  );
}
